import { motion } from "framer-motion";
import { Plus } from "lucide-react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from "@/components/ui/accordion";
import KineticHeading from "@/components/KineticHeading";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { testCategories } from "@/data/tests";
import { LAB } from "@/data/lab";

export default function Tests() {
  return (
    <div data-testid="page-tests">
      <SEO
        title="Diagnostic Tests — Microbiology, Biochemistry, Pathology & More | Lifeline Diagnostics Jaysingpur"
        description="Complete list of diagnostic tests at Lifeline Diagnostics, Jaysingpur — CBC, HbA1c, thyroid, LFT, KFT, lipid, dengue, malaria, TB culture, urine routine, hormone panels, PSA, ANA, TORCH, and more. Every test explained."
        keywords="diagnostic tests Jaysingpur, blood test list, CBC test, thyroid profile, HbA1c, LFT, KFT, lipid profile, dengue test, malaria test, TB culture, PSA test, TORCH panel, ANA test, urine routine, semen analysis, water testing lab Kolhapur"
        path="/tests"
      />
      {/* HERO */}
      <section className="pt-36 md:pt-44 pb-16 md:pb-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-3 mb-8 text-navy"
          >
            <span className="h-px w-10 bg-navy" />
            <span className="font-mono-tag">— Directory of Tests</span>
          </motion.div>

          <KineticHeading
            lines={["Every test", "we perform,", <em key="e" className="italic font-medium text-cyan-brand">explained.</em>]}
            className="font-display text-[13vw] md:text-[8vw] leading-[0.88] tracking-[-0.03em] text-navy"
          />

          <div className="mt-12 grid grid-cols-12 gap-8">
            <div className="col-span-12 md:col-span-4 md:col-start-8" data-testid="tests-hero-para">
              <p className="text-slate-600 leading-relaxed">
                Tap any category to reveal the tests it contains. Each test
                is accompanied by a short, patient-friendly note on why it
                is ordered and what it tells your doctor.
              </p>
              <a
                href={LAB.phoneHref}
                data-testid="tests-hero-call"
                className="mt-6 inline-block font-mono-tag text-navy border-b border-navy pb-1"
              >
                Call the lab · {LAB.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ACCORDION */}
      <section className="pb-32 md:pb-48">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Accordion
            type="multiple"
            className="tests-accordion border-t hair"
            data-testid="tests-accordion"
          >
            {testCategories.map((cat) => (
              <AccordionItem
                key={cat.id}
                value={cat.id}
                data-testid={`tests-category-${cat.id}`}
                className="border-b hair"
              >
                <AccordionTrigger
                  data-testid={`tests-trigger-${cat.id}`}
                  className="group py-8 md:py-10 hover:no-underline [&>svg]:hidden text-left"
                >
                  <div className="flex w-full items-baseline gap-4 md:gap-8">
                    <span className="font-mono-tag text-slate-400 shrink-0 w-10">
                      {cat.number}
                    </span>
                    <div className="flex-1 flex flex-col md:flex-row md:items-baseline md:gap-6">
                      <h3 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[0.95] tracking-tight text-navy group-hover:text-cyan-brand transition-colors duration-500">
                        {cat.title}
                      </h3>
                      <span className="mt-2 md:mt-0 font-mono-tag text-slate-500">
                        {cat.tagline}
                      </span>
                    </div>
                    <div className="shrink-0 ml-auto flex items-center gap-4 md:gap-8">
                      <span className="hidden md:inline font-mono-tag text-slate-400">
                        {cat.tests.length} tests
                      </span>
                      <span className="trigger-plus inline-flex items-center justify-center h-10 w-10 md:h-12 md:w-12 border hair rounded-full transition-transform duration-500 text-navy group-hover:border-cyan-brand group-hover:text-cyan-brand">
                        <Plus className="h-4 w-4" strokeWidth={2} />
                      </span>
                    </div>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="pt-2 pb-12 md:pb-16">
                  <div className="grid grid-cols-12 gap-6 md:gap-8">
                    <div className="col-span-12 md:col-span-3 md:col-start-2">
                      <span className="font-mono-tag text-cyan-brand">
                        {cat.tests.length} tests · {cat.title}
                      </span>
                    </div>
                    <ul className="col-span-12 md:col-span-8 divide-y hair">
                      {cat.tests.map((t, i) => (
                        <li
                          key={i}
                          className="py-5 grid grid-cols-12 gap-6"
                          data-testid={`test-${cat.id}-${i}`}
                        >
                          <div className="col-span-12 md:col-span-5">
                            <span className="font-mono-tag text-slate-400">
                              {String(i + 1).padStart(2, "0")}
                            </span>
                            <div className="mt-2 font-display text-xl md:text-2xl text-navy leading-tight">
                              {t.name}
                            </div>
                          </div>
                          <p className="col-span-12 md:col-span-7 text-slate-600 leading-relaxed">
                            {t.desc}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="mt-16 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <p className="max-w-lg text-slate-600 leading-relaxed">
              Can't find a specific test? Speak with the lab — many
              specialised tests are handled on referral, and Dr. Attar
              will guide you personally.
            </p>
            <a
              href={LAB.phoneHref}
              data-testid="tests-footer-call"
              className="inline-flex items-center gap-3 bg-navy text-white px-5 py-3 text-[13px] font-mono-tag hover:bg-cyan-brand transition-colors duration-500"
            >
              Call {LAB.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
